import { DEFAULT_LOCALE, type Locale } from '../i18n/resources';
import { translateForLocale } from '../i18n/translate';
import { toast } from '../toast/toast';
import { resolveApiErrorMessage } from './apiErrorMessage';
import { handlePossibleTenantDeactivatedError } from './tenantAccessBlocked';

function readLocaleFromDom(): Locale {
  const lang = document.documentElement.getAttribute('lang');
  if (lang === 'en' || lang === 'de') {
    return lang;
  }
  return DEFAULT_LOCALE;
}

/**
 * Show an error toast for a failed mutation.
 * Prefers the API/network message; otherwise uses `fallbackDescription`.
 * No-ops when the error is handled as tenant-deactivated.
 */
export async function toastMutationError(
  error: unknown,
  title: string,
  fallbackDescription = '',
): Promise<void> {
  if (await handlePossibleTenantDeactivatedError(error)) {
    return;
  }

  const apiMessage = await resolveApiErrorMessage(error);
  toast.error({
    title,
    description: apiMessage !== '' ? apiMessage : fallbackDescription,
  });
}

/**
 * Same as `toastMutationError`, but resolves i18n keys from `document.lang`.
 * Use when the caller cannot call `useI18n` (e.g. inside `UserConfigProvider`).
 */
export async function toastMutationErrorKeys(
  error: unknown,
  titleKey: string,
  fallbackDescriptionKey = '',
): Promise<void> {
  const locale = readLocaleFromDom();
  await toastMutationError(
    error,
    translateForLocale(locale, titleKey),
    fallbackDescriptionKey === ''
      ? ''
      : translateForLocale(locale, fallbackDescriptionKey),
  );
}
